import { Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/Tooltip"
import { Avatar } from "@/components/ui/Avatar"
import PeoplePickerPopover from "@/components/PeoplePickerPopover"
import type { Person } from "@/lib/person"

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
// The dashed "+" add button, sized to match the avatars beside it.
const CIRCLE = "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-medium"

function Circle({ person }: { person: Person }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Avatar
          size="md"
          tabIndex={0}
          aria-label={person.name}
          name={person.name}
          initials={person.initials}
          avatarUrl={person.avatar_url}
          className={cn("border border-border", FOCUS)}
        />
      </TooltipTrigger>
      <TooltipContent>{person.name}</TooltipContent>
    </Tooltip>
  )
}

// The "assign people" control: a circle per selected person (name on hover)
// and — when the viewer may edit the list — a dashed "+" that opens the
// Trello-style picker (remove from the top section, add from the bottom).
// Shared by the task drawer and the settings cards.
export default function PeopleCircles({ title, people, selectedIds, onChange, editable, disabled = false, emptyLabel, selectedHeading }: {
  // Popover title, e.g. "In charge" or "Reviewer".
  title: string
  people: Person[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  // Whether the "+" shows at all (viewer may change the list).
  editable: boolean
  // "+" stays visible but inert, e.g. while the surrounding form is saving.
  disabled?: boolean
  // Warning pill when the list is empty; omit to show only the "+".
  emptyLabel?: string
  selectedHeading?: string
}) {
  const selected = people.filter((p) => selectedIds.includes(p.id))
  const plus = (
    <button
      type="button"
      aria-label={`Change ${title.toLowerCase()}`}
      disabled={disabled}
      className={cn(
        CIRCLE, FOCUS,
        "border-dashed text-muted-foreground transition-colors hover:border-solid hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-50",
      )}
    >
      <Plus size={14} />
    </button>
  )

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {selected.map((p) => <Circle key={p.id} person={p} />)}
      {selected.length === 0 && emptyLabel && (
        <span className="inline-flex h-8 items-center rounded-full bg-warning-surface px-2.5 text-xs text-warning">
          {emptyLabel}
        </span>
      )}
      {selected.length === 0 && !emptyLabel && !editable && (
        <span className="text-sm text-muted-foreground">No one assigned</span>
      )}
      {editable && (disabled ? plus : (
        <PeoplePickerPopover
          title={title}
          people={people}
          selectedIds={selectedIds}
          onChange={onChange}
          selectedHeading={selectedHeading}
        >
          {plus}
        </PeoplePickerPopover>
      ))}
    </div>
  )
}
